// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React, { useEffect, useRef } from 'react'; // eslint-disable-line no-unused-vars

import { AsyncOverlay, Icon } from '@zapier/common-components';
import { Colors, Duration, Forms } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

import type { ComponentType } from 'react';
import type {
  GetInputPropsOptions,
  GetInputPropsReturn,
  GetToggleButtonOptions,
  GetToggleButtonReturn,
} from 'downshift';

/** @jsx jsx */

const baseCss = css({
  ...Forms.field.typography,
  alignItems: 'center',
  border: Forms.field.border,
  borderRadius: Forms.field.borderRadius,
  boxSizing: 'border-box',
  color: Colors.steel,
  cursor: 'pointer',
  display: 'flex',
  minHeight: Forms.field.minHeight,

  '&:focus-within': {
    borderColor: Colors.heroine,
    outline: 'none',
    transition: `border-color ${Duration.medium} ease`,
  },
});

const errorCss = css({
  backgroundColor: `${Colors.status.error.bg} !important`, // FLX-71: we need `!important` to override global zapier/zapier styles 💩
  borderColor: `${Colors.status.error.border} !important`, // FLX-71: we need `!important` to override global zapier/zapier styles 💩
});

const hasSelectedItemCss = css({
  color: Colors.batmobile,
});

const isLoadingCss = css({
  cursor: 'default',
  opacity: 0.7,
  pointerEvents: 'none',
});

const isDisabledCss = css({
  backgroundColor: Colors.storm,
  cursor: 'default',
  pointerEvents: 'none',
});

const contentCss = isOpen =>
  css({
    cursor: 'text',
    flex: 1,
    opacity: isOpen ? 0.45 : 1,
    padding: Forms.field.padding,
    pointerEvents: 'none',
    position: 'absolute',
  });

const toggleIconCss = css({
  // zapier/zapier has a global style of border-box, which messes us up
  boxSizing: 'content-box',
  cursor: 'pointer',
  padding: Forms.field.padding,
  pointerEvents: 'none',
});

const inputCss = (isOpen: boolean, hideWhenClosed: boolean) =>
  css({
    background: 'transparent',
    height: `calc(${Forms.field.minHeight} - 2px)`,
    lineHeight: `calc(${Forms.field.minHeight} - 2px)`,
    padding: `0 0 0 ${Forms.field.padding}`,

    // get rid of browser built-in input styles:
    border: 'none',
    fontFamily: 'inherit',
    fontSize: '100%',
    minWidth: 20,
    opacity: isOpen ? 1 : hideWhenClosed ? 0 : 1,
    outline: 0,
    width: '100%',
  });

// We can't safely assume that `Item` will always have a specific shape, because consumers that customize
// rendering might have items with additional/different properties flowing through.
type Item = *;

export type Props = {
  /** browser autocomplete */
  autoComplete?: string,
  /** autofocus the input when rendered */
  autoFocus?: boolean,
  /** a11y label */
  ariaLabel?: string,
  /** get props for toggle button */
  getToggleButtonProps: (
    options: GetToggleButtonOptions
  ) => GetToggleButtonReturn,
  /** get props for input element */
  getInputProps: (options: GetInputPropsOptions) => GetInputPropsReturn,
  /**
   * Callback that will be used to generate the string label to be rendered for the currently selected item.
   */
  getLabelForItem: (item: Item) => string,
  /** current selection is an error */
  hasError?: boolean,
  /** id of the input created by this select field. */
  inputId?: string,
  /** current input value */
  inputValue: string,
  /** `true` if the in a loading state */
  isLoading: boolean,
  /** `true` if this selection box is disabled */
  isDisabled: boolean,
  /** `true` if the dropdown menu is open */
  isOpen: boolean,
  /** id of the label that connects to this select field. */
  labelId?: string,
  /** input name attribute */
  name?: string,
  /** Callback for when the input element is blurred. Used to autocomplete current input to a selection. */
  onBlurInput: (*) => void,
  /** toggle the dropdown menu open and closed */
  toggleMenu: () => void,
  /** Placeholder to render inside the select toggle when no item is selected. */
  placeholder: string,
  /** The currently selected item */
  selectedItem: ?Item,
  /**
   * The Component to use for rendering the content of the selection in the selection box.
   */
  SelectionComponent?: ComponentType<*>,
};

/**
 * The custom 'read-only' view of the current selection. This will only render if a custom
 * SelectionComponent is passed in
 */
const Selection = (props: Props) => {
  const SelectionComponent = props.SelectionComponent;
  if (props.inputValue && props.isOpen) {
    return null;
  }

  return (
    <span aria-labelledby={props.labelId} css={contentCss(props.isOpen)}>
      {SelectionComponent && <SelectionComponent {...props} />}
    </span>
  );
};

/**
 * The input area to enter search values.
 */
const Input = (props: Props) => {
  const inputRef = useRef(null);
  useEffect(() => {
    if (props.autoFocus && inputRef.current) {
      // check inputRef.current to make flow happy
      inputRef.current.focus();
    }
  }, [props.autoFocus]);

  // get props from downshift
  const inputProps = props.getInputProps({
    autoComplete: props.autoComplete,
    onBlur: props.onBlurInput,
  });

  // calculate our placeholder
  const placeholder = props.SelectionComponent
    ? ''
    : !props.selectedItem || (!props.isOpen && !props.selectedItem)
    ? props.placeholder
    : props.getLabelForItem(props.selectedItem);

  return (
    <input
      {...inputProps}
      aria-labelledby={props.labelId}
      css={inputCss(props.isOpen, Boolean(props.SelectionComponent))}
      id={props.inputId}
      name={props.name}
      onChange={e => {
        const isAutofill =
          !Object.getPrototypeOf(e.nativeEvent)
            .toString()
            .includes('InputEvent') ||
          // Firefox uses the same event for user input so
          // look at if this element is not focused to know if it is
          // autocomplete (this will not catch all instances)
          (typeof document !== 'undefined' &&
            document.activeElement !== e.target);
        if (isAutofill) {
          // Since this is autofill try to select the
          // autofill value otherwise it will treat as a search filter
          props.onBlurInput(e.target.value);
          // Force blur so that the menu closes
          e.target.blur();
        } else {
          // Normal user text entry
          inputProps.onChange(e);
        }
      }}
      placeholder={placeholder}
      ref={inputRef}
    />
  );
};

const SelectionBox = (props: Props) => {
  const selectionBoxCss = css(
    baseCss,
    !!props.selectedItem && hasSelectedItemCss,
    props.isLoading && isLoadingCss,
    props.isDisabled && isDisabledCss,
    !props.isOpen && props.hasError && errorCss
  );

  return (
    <AsyncOverlay isVisible={props.isLoading}>
      <div
        css={selectionBoxCss}
        onClick={e => {
          // This will double toggle the menu w/o halting the event here
          e.stopPropagation();
          e.preventDefault();
          // Toggle the menu open / close
          props.toggleMenu();
        }}
        role="none"
      >
        <Input {...props} />
        {props.SelectionComponent && <Selection {...props} />}
        {!props.isDisabled && (
          <Icon
            {...props.getToggleButtonProps({
              'aria-label': props.ariaLabel || props.placeholder,
            })}
            css={toggleIconCss}
            name="arrow-down"
          />
        )}
      </div>
    </AsyncOverlay>
  );
};

export default SelectionBox;
